import { Link2Icon } from 'lucide-react'

import { AutomatedMarketBadge } from '../../automated-market-badge/AutomatedMarketBadge'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { ReserveStatus } from '@/features/sparklend/common/data/market-info/reserve-status'
import { ColorFilter } from '@/ui/atoms/color-filter/ColorFilter'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import { FrozenPill } from '@/ui/molecules/frozen-pill/FrozenPill'
import { PausedPill } from '@/ui/molecules/paused-pill/PausedPill'
import { cn } from '@/ui/utils/style'
import { testIds } from '@/ui/utils/testIds'
import { useIsTruncated } from '@/ui/utils/useIsTruncated'

interface ProxyMarketAssetNameCellProps {
  underlyingReserveLogo: string
  tokenSymbol: TokenSymbol
  tokenLogo: string
  name: string
  description: string
  reserveStatus: ReserveStatus
  sllManaged: boolean
  'data-testid': string
}

export function ProxyMarketAssetNameCell({
  tokenSymbol,
  tokenLogo,
  name,
  underlyingReserveLogo,
  description,
  reserveStatus,
  sllManaged,
  'data-testid': dataTestId,
}: ProxyMarketAssetNameCellProps) {
  const isPaused = reserveStatus === 'paused'
  const isFrozen = reserveStatus === 'frozen'

  return (
    <div>
      <div className="relative flex flex-row items-center gap-3 py-2" data-testid={dataTestId}>
        <div className="flex shrink-0">
          <ColorFilter variant={isPaused ? 'red' : 'none'}>
            <div className="relative size-6 md:size-5 lg:size-6">
              <div className="absolute inset-0 overflow-hidden rounded-full">
                <img
                  src={tokenLogo}
                  className="absolute inset-0 size-full rounded-full [clip-path:inset(0_50%_0_0%)]"
                />
                <img
                  src={underlyingReserveLogo}
                  className="absolute inset-0 size-full rounded-full [clip-path:inset(0_0_0_50%)]"
                />
              </div>
            </div>
          </ColorFilter>
        </div>
        <div className="-top-7 pointer-events-none absolute left-[3px] z-10 md:left-0.5 lg:left-[3px]">
          <div className="flex items-center justify-center rounded-full bg-brand-secondary px-[3px] py-1.5">
            <Link2Icon className="icon-size-xxs icon-brand-secondary rotate-90" />
          </div>
        </div>
        <div className="flex min-w-0 gap-2 lg:flex-col xl:flex-row xl:items-center">
          <div className="flex min-w-0 flex-col xl:flex-row xl:items-baseline xl:gap-2">
            <TokenName tokenName={name} className={cn(isPaused && 'text-system-error-secondary')} />
            <p className={cn('typography-label-4 text-secondary', isPaused && 'text-system-error-secondary/60')}>
              {tokenSymbol}
            </p>
          </div>
          {sllManaged && <AutomatedMarketBadge className="shrink-0 self-start" />}
        </div>
        {isFrozen && <FrozenPill data-testid={testIds.markets.frozenPill} />}
        {isPaused && <PausedPill data-testid={testIds.markets.pausedPill} />}
      </div>
      <div className="typography-label-4 text-secondary">{description}</div>
    </div>
  )
}

interface TokenNameProps {
  tokenName: string
  className?: string
}

export function TokenName({ tokenName, className }: TokenNameProps) {
  const [tokenNameRef, isTruncated] = useIsTruncated()

  return (
    <Tooltip open={!isTruncated ? false : undefined}>
      <TooltipTrigger asChild>
        <p className={cn('typography-label-2 truncate', className)} ref={tokenNameRef}>
          {tokenName}
        </p>
      </TooltipTrigger>
      <TooltipContent>{tokenName}</TooltipContent>
    </Tooltip>
  )
}
